import { v4 as uuidv4 } from 'uuid';
import { auth, db, storage, functionsBaseUrl } from '@/firebase';
import {
	AVATAR_USE_CASE,
	Avatar,
	IMAGE_UPLOAD_MAX_BYTES,
	RUNNIT_MASK_USE_CASE,
	RunnitMask,
	RunnitUpload,
	THUMB_SIZE,
	UPLOAD_FILE_TYPE,
} from '@run-diffusion/shared';
import {
	getAsset3DExtension,
	inferAsset3DContentType,
	isAsset3DFileName,
} from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFormats';
import { renderAsset3DStaticPreviewBlob } from '@/views/Runnits/RunnitSettings/Asset3D/babylonAsset3D';
import { requiresServerNormalization, resolveUploadContentType } from '@/utils/imageUtils';
import { uploadMedia, UploadKind } from '@/utils/uploadApi';
import {
	UploadImageError,
	UploadImageFailureKind,
	formatUploadLimit,
	formatUploadSize,
	preflightUploadSize,
	toUploadImageError,
} from '@/utils/uploadErrors';

// Compatibility for anything still importing these from here. The error surface
// lives in uploadErrors.ts, which cannot import this module back. Delete these
// once nothing reads them from here.
export { UploadImageError, formatUploadLimit, formatUploadSize };
export type { UploadImageFailureKind };

export interface ImageMetaData {
	name: string;
	size: number;
	contentType: string;
	width: number;
	height: number;
}

export interface VideoMetaData {
	name: string;
	contentType: string;
	size: number;
	width: number;
	height: number;
	duration: number;
}

export type UploadImageUseCase = 'RUNNIT_UPLOAD' | 'AVATAR' | 'RUNNIT_MASK';
export type UploadImageRecordUseCase = keyof typeof AVATAR_USE_CASE | keyof typeof RUNNIT_MASK_USE_CASE;

type UploadImageRecordCollection = 'runnitUploads' | 'avatars' | 'runnitMasks';
type UploadImageResponseBody = {
	normalizedStoragePath: string;
	normalizedFileName: string;
	contentType: string;
	size: number;
	width: number;
	height: number;
	wasConverted: boolean;
	hashHex: string;
	deduped: boolean;
	recordId: string;
	recordCollection: UploadImageRecordCollection;
	recordData: Record<string, unknown>;
};

type UploadImageForUseCaseOptions = {
	file: File;
	uploadType: UploadImageUseCase;
	useCase?: UploadImageRecordUseCase;
};

const UPLOAD_IMAGE_URL: string = `${functionsBaseUrl}/uploadImage`;
// Generous enough to push a ~28MB body over a slow link, bounded so a proxy that
// accepts and never answers surfaces as an error instead of an endless spinner.
const UPLOAD_REQUEST_TIMEOUT_MS: number = 120000;

type UploadErrorBody = {
	message: string;
	/** Only our own function's `{ error }` string is safe to show a user. */
	isTrusted: boolean;
	errorId: string | null;
};

/** Bound on any body text we retain, so a full HTML error page never reaches a log line. */
const MAX_RETAINED_ERROR_BODY_CHARS: number = 300;

/** The function replies `{ error, errorId? }`; anything else is captured for diagnostics only. */
const readUploadErrorBody = async (response: Response): Promise<UploadErrorBody> => {
	let body: string = '';
	try {
		body = await response.text();
	} catch {
		return { message: '', isTrusted: false, errorId: null };
	}
	try {
		const parsed = JSON.parse(body);
		const error: string = String(parsed?.error || '').trim();
		if (error) {
			return {
				message: error,
				isTrusted: true,
				errorId: parsed?.errorId ? String(parsed.errorId) : null,
			};
		}
		// Valid JSON, but not our shape: a gateway or WAF speaking its own schema.
		// Keep the raw body so the real reason survives into logs and telemetry.
		return { message: body.trim().slice(0, MAX_RETAINED_ERROR_BODY_CHARS), isTrusted: false, errorId: null };
	} catch {
		// Not JSON: an intercepting proxy or the platform's own error page.
		return { message: body.trim().slice(0, MAX_RETAINED_ERROR_BODY_CHARS), isTrusted: false, errorId: null };
	}
};

const postImageUploadRequest = async (
	url: string,
	file: File,
	uploadType: UploadImageUseCase,
	useCase?: UploadImageRecordUseCase,
): Promise<Response> => {
	const currentUser = auth.currentUser;
	if (!currentUser) {
		throw new UploadImageError('NOT_SIGNED_IN', 'User must be authenticated to upload');
	}
	let idToken: string;
	try {
		idToken = await currentUser.getIdToken();
	} catch (err: unknown) {
		throw new UploadImageError(
			'NOT_SIGNED_IN',
			'Could not refresh your session token',
			{ cause: err },
		);
	}
	const params = new URLSearchParams({
		uploadType,
		fileName: file.name,
		contentType: file.type || 'application/octet-stream',
	});
	if (useCase) {
		params.set('useCase', useCase);
	}
	// A proxy that accepts the connection and never replies would otherwise leave
	// this promise pending forever, and every caller awaiting it hangs with no
	// error and no telemetry. Bound it explicitly.
	const controller: AbortController = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), UPLOAD_REQUEST_TIMEOUT_MS);
	try {
		return await fetch(`${url}?${params.toString()}`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${idToken}`,
				'Content-Type': 'application/octet-stream',
			},
			body: file,
			signal: controller.signal,
		});
	} catch (err: unknown) {
		// fetch only rejects on a transport-level failure. The browser deliberately
		// hides which one it was, so all we can honestly say is that the request
		// never got a reply: offline, a corporate proxy blocking
		// cloudfunctions.net, or a CORS rejection all land here. An abort is the
		// one case we can name precisely, because we caused it.
		const timedOut: boolean = (err as { name?: string } | null)?.name === 'AbortError';
		throw new UploadImageError(
			'NETWORK_UNREACHABLE',
			timedOut
				? `Upload timed out after ${Math.round(UPLOAD_REQUEST_TIMEOUT_MS / 1000)}s`
				: 'Could not reach the upload service',
			{ cause: err },
		);
	} finally {
		clearTimeout(timeoutId);
	}
};

export const uploadImageViaServer = async (
	file: File,
	uploadType: UploadImageUseCase,
	useCase?: UploadImageRecordUseCase,
): Promise<UploadImageResponseBody> => {
	// Pre-flight the hard server cap so an oversized body gets a real explanation
	// instead of a 413 (or a bodiless gen2 rejection that surfaces as a CORS error).
	if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
		throw new UploadImageError(
			'TOO_LARGE',
			`Image is ${formatUploadSize(file.size)}, over the ${formatUploadLimit(IMAGE_UPLOAD_MAX_BYTES)} upload limit`,
			{ status: 413 },
		);
	}
	const response: Response = await postImageUploadRequest(UPLOAD_IMAGE_URL, file, uploadType, useCase);
	if (!response.ok) {
		const { message, isTrusted, errorId } = await readUploadErrorBody(response);
		const kind: UploadImageFailureKind = response.status === 413
			? 'TOO_LARGE'
			: response.status === 401 || response.status === 403
				? 'NOT_SIGNED_IN'
				: response.status >= 500
					? 'SERVER_ERROR'
					: 'REJECTED';
		throw new UploadImageError(
			kind,
			message || `Upload failed with status ${response.status}`,
			{ status: response.status, serverMessage: message, isTrustedServerMessage: isTrusted, errorId },
		);
	}
	try {
		return await response.json();
	} catch (err: unknown) {
		throw new UploadImageError(
			'SERVER_ERROR',
			'Upload service returned an unreadable response',
			{ status: response.status, cause: err },
		);
	}
};

const UPLOAD_KIND_BY_TYPE: Record<UploadImageUseCase, UploadKind> = {
	RUNNIT_UPLOAD: 'runnitUpload',
	AVATAR: 'avatar',
	RUNNIT_MASK: 'mask',
};

const sha256Hex = async (file: File): Promise<string> => {
	const fileData: ArrayBuffer = await file.arrayBuffer();
	const hashBuffer: ArrayBuffer = await crypto.subtle.digest('SHA-256', fileData);
	return Array.from(new Uint8Array(hashBuffer))
		.map((byte: number) => byte.toString(16).padStart(2, '0'))
		.join('');
};

export async function uploadImageRecordForUseCase<T extends RunnitUpload | Avatar | RunnitMask> ({
	file,
	uploadType,
	useCase,
}: UploadImageForUseCaseOptions): Promise<T> {
	// No browser can display HEIC, HEIF, TIFF or BMP, so those keep the POST
	// path: uploadImage is the only place that re-encodes them to JPEG. Its own
	// 28MiB pre-flight already produces a correctly worded refusal.
	if (requiresServerNormalization(file)) {
		const response: UploadImageResponseBody = await uploadImageViaServer(file, uploadType, useCase);
		return {
			...response.recordData,
			get id () { return response.recordId; },
		} as T;
	}

	// Everything else goes straight to storage and is kept byte for byte. Masks
	// and annotations land here too, which is what preserves their exact canvas
	// dimensions: nothing on this path decodes or rescales.
	//
	// preflightUploadSize resolves to the signed ceiling for anything reaching
	// this branch. ImageUpload.vue already called it, but the other five callers
	// did not, and uploadMediaInit's own refusal carries no size.
	preflightUploadSize(file);

	// Sequential, not Promise.all. Both steps hold the whole file: the digest
	// needs a full ArrayBuffer and the decode needs width*height*4 bytes, which
	// for a 100MB source is hundreds of megabytes each. Overlapping them doubles
	// the peak for no wall-clock worth having.
	let width: number;
	let height: number;
	let hashHex: string;
	try {
		const metaData: ImageMetaData = await getImageMetaData(file);
		width = metaData.width;
		height = metaData.height;
		hashHex = await sha256Hex(file);
	} catch (err: unknown) {
		throw new UploadImageError(
			'REJECTED',
			'This image could not be read. It may be corrupt or in a format this browser cannot open.',
			{ cause: err },
		);
	}

	try {
		const record: Record<string, any> = await uploadMedia({
			kind: UPLOAD_KIND_BY_TYPE[uploadType],
			file,
			hashHex,
			uploadType: 'IMG',
			useCase: useCase || null,
			width,
			height,
			contentType: resolveUploadContentType(file),
			teamId: null,
		});
		return record as T;
	} catch (err: unknown) {
		// Restore the classification IMG-2026-025 built on the POST path, so
		// annotationSaveErrors still tells apart a session, a network, a refusal
		// and a server fault.
		throw toUploadImageError(err);
	}
}

/**
 * Get image dimensions and file metadata (for runnit upload record).
 */
export function getImageMetaData (file: File): Promise<ImageMetaData> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		const tempUrl = URL.createObjectURL(file);
		image.src = tempUrl;
		image.onload = () => {
			resolve({
				name: file.name,
				size: file.size,
				contentType: file.type,
				width: image.width,
				height: image.height,
			});
			URL.revokeObjectURL(tempUrl);
		};
		image.onerror = () => {
			reject(new Error('Failed to load image for metadata'));
			URL.revokeObjectURL(tempUrl);
		};
	});
}

/**
 * Get video dimensions, duration, and file metadata (for runnit upload record).
 */
export function getVideoMetaData (file: File): Promise<VideoMetaData> {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.onloadedmetadata = () => {
			window.URL.revokeObjectURL(video.src);
			resolve({
				name: file.name,
				contentType: file.type,
				size: file.size,
				width: video.videoWidth,
				height: video.videoHeight,
				duration: video.duration,
			});
		};
		video.onerror = () => {
			window.URL.revokeObjectURL(video.src);
			reject(new Error('Failed to load video metadata'));
		};
		video.src = URL.createObjectURL(file);
	});
}

async function findExistingUploadByHash (hashHex: string, userId: string): Promise<RunnitUpload | null> {
	const snapshot = await db
		.collection('runnitUploads')
		.where('hashHex', '==', hashHex)
		.where('userId', '==', userId)
		.where('deletedAt', '==', null)
		.limit(1)
		.get();

	if (snapshot.empty) return null;
	const doc = snapshot.docs[0];
	return {
		...doc.data(),
		get id () { return doc.id; },
	} as RunnitUpload;
}

/**
 * Upload an image file to runnit (or return existing upload if same hash for user).
 * The uploaded object is normalized server-side (HEIC/HEIF/etc -> JPEG) before metadata is stored.
 */
export async function uploadImageToRunnit (file: File): Promise<RunnitUpload> {
	return await uploadImageRecordForUseCase<RunnitUpload>({
		file,
		uploadType: 'RUNNIT_UPLOAD',
	});
}

/**
 * Upload a video file to runnit (or return existing upload if same hash for user).
 * Records to runnitUploads with type VID.
 */
export async function uploadVideoToRunnit (file: File, userId: string): Promise<RunnitUpload> {
	const fileData = await file.arrayBuffer();
	const hashBuffer = await crypto.subtle.digest('SHA-256', fileData);
	const hashHex = Array.from(new Uint8Array(hashBuffer))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');

	const existing = await findExistingUploadByHash(hashHex, userId);
	if (existing) return existing;

	const { name, contentType, size, width, height, duration } = await getVideoMetaData(file);
	const ext = name.split('.').pop() ?? 'mp4';
	const uniqName = `${uuidv4()}.${ext}`;
	const fileRef = storage.ref(`runnitUploads/users/${userId}/uploads/${uniqName}`);
	await fileRef.put(file);

	const nowDate = new Date();
	const addedRef = await db.collection('runnitUploads').add({
		createdAt: nowDate,
		deletedAt: null,
		userId,
		teamId: null,
		type: UPLOAD_FILE_TYPE.VID,
		name: uniqName,
		contentType,
		size,
		width,
		height,
		duration,
		hashHex,
	});
	const snap = await addedRef.get();
	return {
		...snap.data(),
		get id () { return addedRef.id; },
	} as RunnitUpload;
}

/** Re-encode a preview blob to WebP so it matches the `_512x512.webp` thumbnail convention. */
async function blobToWebpBlob (blob: Blob, size: number): Promise<Blob> {
	const bitmap = await createImageBitmap(blob);
	try {
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		if (!ctx) throw new Error('Canvas 2D context unavailable');
		ctx.drawImage(bitmap, 0, 0, size, size);
		return await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('WebP encode failed'))), 'image/webp');
		});
	} finally {
		bitmap.close();
	}
}

// how long an upload waits for its 3D model's preview before going on without one
const ASSET_3D_PREVIEW_TIMEOUT_MS = 20000;

/**
 * Upload a self-contained 3D asset to runnit (or return existing upload if same hash for user).
 * Records to runnitUploads with type ASSET_3D; the static preview is stored at the
 * derived thumbnail path (like image/video thumbs), not as a displayFile.
 */
export async function uploadAsset3DToRunnit (file: File, userId: string): Promise<RunnitUpload> {
	if (!isAsset3DFileName(file.name)) {
		throw new Error('Unsupported 3D asset format');
	}

	const fileData = await file.arrayBuffer();
	const hashBuffer = await crypto.subtle.digest('SHA-256', fileData);
	const hashHex = Array.from(new Uint8Array(hashBuffer))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');

	const existing = await findExistingUploadByHash(hashHex, userId);
	if (existing) return existing;

	const ext = getAsset3DExtension(file.name) || 'glb';
	const uniqName = `${uuidv4()}.${ext}`;
	const contentType = inferAsset3DContentType(file);
	const fileRef = storage.ref(`runnitUploads/users/${userId}/uploads/${uniqName}`);
	await fileRef.put(file, { contentType });

	// Render the static preview client-side (Babylon — no server memory concern)
	// and store it at the derived thumbnail path so the frontend reads it the same
	// way as image/video thumbnails (getRunnitUploadFilePath(...).thumbFilePath),
	// rather than a bespoke displayFile. Best-effort: on failure the tile falls
	// back to the static cube icon.
	try {
		// bounded, as the preview is best-effort: an upload, and the board's 3D import, never waits on it
		// for good (P3-13); the renderer lets go of its viewer however it ends
		const previewBlob = await renderAsset3DStaticPreviewBlob(file, { width: THUMB_SIZE, height: THUMB_SIZE, timeoutMs: ASSET_3D_PREVIEW_TIMEOUT_MS });
		const webpBlob = await blobToWebpBlob(previewBlob, THUMB_SIZE);
		const thumbName = uniqName.replace(/\.[^/.]+$/, `_${THUMB_SIZE}x${THUMB_SIZE}.webp`);
		const thumbRef = storage.ref(`runnitUploads/users/${userId}/uploads/thumbs/${thumbName}`);
		await thumbRef.put(webpBlob, { contentType: 'image/webp' });
	} catch (err) {
		console.warn('ASSET_3D preview generation failed; falling back to static icon tile', err);
	}

	const nowDate = new Date();
	const addedRef = await db.collection('runnitUploads').add({
		createdAt: nowDate,
		deletedAt: null,
		userId,
		teamId: null,
		type: UPLOAD_FILE_TYPE.ASSET_3D,
		name: uniqName,
		contentType,
		size: file.size,
		hashHex,
	});
	const snap = await addedRef.get();
	return {
		...snap.data(),
		get id () { return addedRef.id; },
	} as RunnitUpload;
}
